import {
  BaseLayerTypeEnumValues,
  ContactTypeEnumValues,
  EngagementOptionEnum,
  EngagementOptionEnumValues,
  ExchangeTypeEnumValues,
  IntegrationStatusEnumValues,
  MarketMakerQuoteKpiMetricEnumValues,
  MarketMakerQuoteLoanCustodyEnumValues,
  MarketMakerQuoteStatusEnumValues,
  MarketMakerQuoteTranchePriceMethodologyEnumValues,
  MarketMakerRequestStatusEnumValues,
  OptionsStyleEnumValues,
  MMRFQTranchePriceFlagEnumValues,
} from '@forgd/shared/enums'
import { PerformanceGradeValues } from '@forgd/shared/leaderboard/performanceGrade'
import { KpiTierEnumValues } from '@forgd/shared/rfq/calculations/kpi-calculator'
import { isFloatGreaterWithPrecision, isFloatLessWithPrecision } from '@forgd/shared/utils/numbers'
import { z, ZodIssueCode } from 'zod'
import { MetaSchema } from '../calculator'
import { customDateSchema, likertScaleTypeSchema } from '../common'
import { KpiResultSchema } from '../kpi'
import { MarketMakerDetailsSchema } from '../market-maker'
import { LeaderboardBreakdownResponseSchema, PerformanceMetricsForMmResponseSchema } from '../market-maker/leaderboard'
import { PriceDataSchema, TokenInfoResult } from '../token-unlock'
import { formatValue } from '@forgd/formatter'

export interface MarketMakerRFQOption {
  value: string
  label: string
  description?: string
  engagementDescription?: string
  loanRequirements?: string[]
}

export const OptionResult = z.object({
  value: z.string(),
  label: z.string(),
})

export type Investor = z.infer<typeof OptionResult>

export const InvestorsResult = z.array(OptionResult)

export const RFQProjectDetailsResult = z.object({
  id: z.string().uuid(),
  isResearch: z.boolean(),
  ticker: z.string().nullable(),
  tokenListed: z.boolean().nullable().default(false),
  link: z.string().nullable(),
  coingeckoId: z.string().nullable(),
  isTdPublished: z.boolean(),
  hasRFQ: z.boolean(),
  tgeDate: z.string().nullable(),
  image: z.string().nullable(),
  name: z.string().nullable(),
  baseLayer: z.array(z.enum(BaseLayerTypeEnumValues)).nullable(),
  hasEngagedMarketMakers: z.boolean(),
})
export type RFQProjectDetails = z.infer<typeof RFQProjectDetailsResult>

export const RFQTokenDesignerDetailsResult = z.object({
  maxTokenSupply: z.number().safe().nullable(),
  fdvAtTge: z.number().safe().nullable(),
  tokenPriceAtTGE: z.number().safe().nullable(),
  percentageUnlockedAtTGE: z.number().safe(),
  circulatingSupply: z.number().safe().nullable(),
  marketCapAtTge: z.number().safe().nullable(),
})

export type RFQTokenDesignerDetails = z.infer<typeof RFQTokenDesignerDetailsResult>

export const RFQTokenMarketDetailsResult = TokenInfoResult.omit({
  tgeDate: true,
}).merge(
  z.object({
    fdv: z.number().safe(),
    circulatingSupply: z.number().safe(),
    marketCap: z.number().safe().nullable(),
    price: z.number().safe(),
    priceChangePercentage24h: z.number().safe(),
    marketChartPrice24h: z
      .object({
        data: z.array(PriceDataSchema),
        meta: MetaSchema,
      })
      .nullable(),
  }),
)

export type RFQTokenMarketDetails = z.infer<typeof RFQTokenMarketDetailsResult>

export const RFQStatusItemResult = z.object({
  status: z.enum(MarketMakerRequestStatusEnumValues),
  date: z.string(),
})

export type RFQStatusItem = z.infer<typeof RFQStatusItemResult>

export const RFQResult = z.object({
  status: z.enum(MarketMakerRequestStatusEnumValues),
  statusUpdatedAt: z.string(),
  marketMakersQty: z.number().min(1).max(5),
  engagementOptions: z.array(z.enum(EngagementOptionEnumValues)).nullable(),
  tokenSupplyPercentageToAllocate: z.string().min(1),
  stableCoinBudgetToAllocate: z.string().min(1).nullable(),
  liquidityDistributionCex: z.number().int().nonnegative().max(100).nullable().optional(),
  liquidityDistributionDex: z.number().int().nonnegative().max(100).nullable().optional(),
  motivations: z.array(z.string()),
  exchangesCEX: z.array(z.string()),
  exchangesDEX: z.array(z.string()),
  externalCapital: z.string().nullable(),
  investors: z.array(z.string()).optional(),
  country: z.string().min(2),
  hasLegalOpinionOnUtilityStatus: z.boolean(),
  maxDaysForMarketMakerToSubmitQuotes: z.number().safe().int().positive(),
  round: z.number().safe(),
  shareTdPublishedPage: z.boolean().nullable(),
  hasExchangeSpecialists: z.boolean().nullable(),
  specialistsCEX: z.number().safe().nullable(),
  specialistsDEX: z.number().safe().nullable(),
  hasSupportExchanges: z.boolean().nullable(),
  supportExchangesCEX: z.array(z.string()).nullable(),
  supportExchangesDEX: z.array(z.string()).nullable(),
  maxSelectedQuotes: z.number().int().positive().nullable(),
  preferLiquidityDepth: z.number().nullable().optional(),
  preferMarketMakerQuality: z.number().nullable().optional(),
  preferStablecoinEfficiency: z.number().nullable().optional(),
  preferTokenPurchasePrice: z.number().nullable().optional(),
  isReopenedForMe: z.boolean().optional(),
  reopenDeadline: z.string().nullable().optional(),
})

export type RFQ = z.infer<typeof RFQResult>

export const RFQDetailsResult = z.object({
  project: RFQProjectDetailsResult,
  tokenDesignerDetails: RFQTokenDesignerDetailsResult.optional(),
  tokenMarketDetails: RFQTokenMarketDetailsResult.optional(),
  statuses: z.array(RFQStatusItemResult),
  rfq: RFQResult.nullable(),
})

export type RFQDetails = z.infer<typeof RFQDetailsResult>

export const ExchangeResult = OptionResult.extend({
  url: z.string().nullable().optional(),
  type: z.enum(ExchangeTypeEnumValues),
  order: z.number().safe().nullable(),
})

export type Exchange = z.infer<typeof ExchangeResult>

export const ExchangesResult = z.array(ExchangeResult)

const MarketMakerResult = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  imageUrl: z.string().nullable(),
  engagementOptions: z.array(z.enum(EngagementOptionEnumValues)),
  integrationStatus: z.enum(IntegrationStatusEnumValues),
  supportedCEX: ExchangesResult,
  supportedDEX: ExchangesResult,
  services: z.array(z.string()),
})

export type MarketMaker = z.infer<typeof MarketMakerResult>

export const MarketMakersResult = z.array(MarketMakerResult)

export const MarketMakerRFQBodyBase = z.object({
  calendarLink: z.preprocess(
    (val) => (val === '' ? null : val),
    z
      .string()
      .url()
      .refine((url) => {
        try {
          const { hostname } = new URL(url)
          return hostname.includes('.')
        } catch {
          return false
        }
      }, 'Invalid url')
      .nullable()
      .default(null),
  ),
  circulatingSupply: z.number().safe().nullable(),
  coingeckoUrl: z.preprocess((val) => (val === '' ? null : val), z.string().url().nullable().default(null)),
  confirmedListingExchanges: z.array(z.string()).optional(),
  contact: z.string().min(1, 'Contact is required'),
  contactType: z.enum(ContactTypeEnumValues),
  country: z.string().min(2, 'Country is required'),
  engagementOptions: z.array(z.enum(EngagementOptionEnumValues)),
  exchangesCEX: z.array(z.string()),
  exchangesDEX: z.array(z.string()),
  externalCapital: z.string().min(1, 'The Amount of external capital is required'),
  fdv: z.number().safe().nullable(),
  hasCoingeckoUrl: z.boolean().optional().default(false),
  hasExchangeSpecialists: z.boolean().optional().default(true),
  hasLegalOpinionOnUtilityStatus: z.boolean({ message: 'Legal opinion is required' }),
  hasMarketMakerEngagement: z.boolean({ message: 'Please select an option' }),
  hasSupportExchanges: z.boolean().optional().default(true),
  investors: z.array(z.string().uuid()).optional(),
  isEngagementUndecided: z.boolean().optional(),
  isTokenIssuedInUsTerritory: z.boolean().nullable().optional(),
  liquidityDistributionCex: z.number().int().nonnegative().max(100).nullable().optional(),
  liquidityDistributionDex: z.number().int().nonnegative().max(100).nullable().optional(),
  marketMakersQty: z.number({ message: 'Select at least 1 and up to 5 market makers' }).min(1).max(5),
  motivations: z.array(z.string()).min(1, { message: 'Select at least 1 motivation' }),
  mts: z.number().safe().nullable(),
  otherCex: z.array(z.string()),
  otherDex: z.array(z.string()),
  otherMarketMakers: z.array(z.string()),
  percentageUnlockedAtTge: z.number().optional().nullable().default(10),
  preferLiquidityDepth: likertScaleTypeSchema,
  preferMarketMakerQuality: likertScaleTypeSchema,
  preferStablecoinEfficiency: likertScaleTypeSchema.nullable().default(null),
  preferTokenPurchasePrice: likertScaleTypeSchema.nullable().default(null),
  services: z.array(z.string()).min(1, { message: 'Select at least 1 service' }),
  shareTdPublishedPage: z.boolean().optional().default(false),
  specialistsCEX: z.number().safe().nullable(),
  specialistsDEX: z.number().safe().nullable(),
  stableCoinBudgetToAllocate: z
    .string()
    .min(1, { message: 'Please select an estimated stable coin budget allocation' })
    .nullable(),
  supportExchangesCEX: z.array(z.string()).nullable(),
  supportExchangesDEX: z.array(z.string()).nullable(),
  tgeDate: z.string().date().nullable().default(null),
  tokenomicsUrl: z.preprocess((val) => (val === '' ? null : val), z.string().url().nullable().default(null)),
  tokenSupplyPercentageToAllocate: z.string().min(1, { message: 'Please select an estimated token supply allocation' }),
})

export const MarketMakerRFQBody = MarketMakerRFQBodyBase.superRefine((data, ctx) => {
  const liquidityDistributionCex = data.liquidityDistributionCex
  const liquidityDistributionDex = data.liquidityDistributionDex
  const hasLiquidityDistributionCex = liquidityDistributionCex != null
  const hasLiquidityDistributionDex = liquidityDistributionDex != null

  if (data.isEngagementUndecided && data.engagementOptions.length > 0) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      path: ['engagementOptions'],
      message: 'Undecided RFQs must not include engagement options',
    })
  }

  if (!data.isEngagementUndecided && data.engagementOptions.length === 0) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      path: ['engagementOptions'],
      message: 'Select at least 1 engagement',
    })
  }

  if (
    data.engagementOptions.includes(EngagementOptionEnum.RetainerWorkingCapital) &&
    !data.stableCoinBudgetToAllocate
  ) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      path: ['stableCoinBudgetToAllocate'],
      message: 'Please select an estimated stablecoin budget',
    })
  }

  if (hasLiquidityDistributionCex !== hasLiquidityDistributionDex) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      path: ['liquidityDistributionCex'],
      message: 'Liquidity distribution must include both CEX and DEX percentages',
    })
    return
  }

  if (
    hasLiquidityDistributionCex &&
    hasLiquidityDistributionDex &&
    liquidityDistributionCex + liquidityDistributionDex !== 100
  ) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      path: ['liquidityDistributionCex'],
      message: 'Liquidity distribution must equal 100%',
    })
  }
})

export type MarketMakerRFQ = z.infer<typeof MarketMakerRFQBody>

const QuoteDMMSchema = z.object({
  quoteId: z.string(),
  onboardingFeeUsd: z
    .number()
    .int({ message: 'Onboarding Fee value must be a non-negative integer.' })
    .nonnegative()
    .finite(),
  unlimitedExchangeCoverage: z.boolean(),
  unlimitedExchangeCoverageMonthlyFeeUsd: z
    .number()
    .int({ message: 'Monthly Fee value must be a non-negative integer.' })
    .nonnegative()
    .finite(),
  singleExchangeCoverageMonthlyFeeUsd: z
    .number()
    .int({ message: 'Monthly Fee value must be a non-negative integer.' })
    .nonnegative()
    .finite(),
  minContractTermMonths: z.number().safe().int().nonnegative().finite(),
  profitSharePercentage: z.number().safe().nonnegative().finite(),
  loanCustody: z.enum(MarketMakerQuoteLoanCustodyEnumValues),
})

export const ViewSchemaResponse = z.object({
  value: z.number().safe(),
  unit: z.string(),
})

export const MetricSchemaResponse = z.object({
  metric: z.enum(MarketMakerQuoteKpiMetricEnumValues),
  views: z.object({
    percent: ViewSchemaResponse,
    usd: ViewSchemaResponse,
  }),
})

export const TierSchemaResponse = z.object({
  tier: z.enum(KpiTierEnumValues),
  data: z.array(MetricSchemaResponse),
})

export type TierSchema = z.infer<typeof TierSchemaResponse>
export const RFQQuotesSummarySchema = z.object({
  quotesReceptionDate: z.string().date().optional(),
  quotesCount: z.number().safe(),
  engagements: z.object({
    [EngagementOptionEnum.LoanCallOption]: z.object({
      quotesCount: z.number().safe(),
      avgTokenLoanSize: z.number().safe(),
      avgTokenLoanSupplyPercentage: z.number().safe(),
      europeanOptionsCount: z.number().safe(),
      americanOptionsCount: z.number().safe(),
      avgLoanTenorMonths: z.number().safe(),
      avgTokenLoanSizeUsd: z.number().safe(),
      avgOptionPremiumPerQuote: z.number().safe(),
      avgInterestRatePercentage: z.number().safe(),
      avgNumberOfTranches: z.number().safe(),
      tranchesFixedPricePercentage: z.number().safe(),
      tranchesDynamicPricePercentage: z.number().safe(),
      lessThan1MonthPercentage: z.number().safe(),
      between1And6MonthsPercentage: z.number().safe(),
      greaterThan6MonthsPercentage: z.number().safe(),
      avgOrderBookDominanceMax: z.number().safe(),
      skewCexCount: z.number().safe(),
      skewDexCount: z.number().safe(),
      skewEvenCount: z.number().safe(),
    }),
    [EngagementOptionEnum.RetainerWorkingCapital]: z.object({
      quotesCount: z.number().safe(),
      avgTokenLoanSize: z.number().safe(),
      avgTokenLoanSupplyPercentage: z.number().safe(),
      avgStablecoinLoanSizeUsd: z.number().safe(),
      avgTokenLoanSizeUsd: z.number().safe(),
      avgOnboardingFeeUsd: z.number().safe(),
      avgMonthlyFeeUsd: z.number().safe(),
      avgProfitSharePercent: z.number().safe(),
      avgTotalLoanSizeUsd: z.number().safe(),
      quotesWithUnlimitedExchangeCoveragePercentage: z.number().safe(),
      quotesWithFeesChargedPerExchangePercentage: z.number().safe(),
      skewCexCount: z.number().safe(),
      skewDexCount: z.number().safe(),
      skewEvenCount: z.number().safe(),
    }),
  }),
})

export type RFQQuotesSummaryType = z.infer<typeof RFQQuotesSummarySchema>

export const RFQRoundDetailsResponse = z.object({
  quotesSummary: RFQQuotesSummarySchema,
  kpisSummary: z.array(TierSchemaResponse),
})

export type RFQRoundDetails = z.infer<typeof RFQRoundDetailsResponse>

export const LoanCallOptionTranche = z.object({
  trancheNumber: z.number().safe().int().min(0).finite(),
  tokenSupplyPercentage: z.number().safe(),
  priceMethodology: z.enum(MarketMakerQuoteTranchePriceMethodologyEnumValues),
  price: z.number().safe(),
  priceFlag: z.enum(MMRFQTranchePriceFlagEnumValues),
  twapThresholdDays: z.number().safe().nullable(),
  timePeriod: z.number().safe(),
})

export type LoanCallOptionTrancheSchema = z.infer<typeof LoanCallOptionTranche>

export const BaseQuoteSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(MarketMakerQuoteStatusEnumValues),
  engagementType: z.enum(EngagementOptionEnumValues),
  marketMakerName: z.string(),
  marketMakerId: z.string(),
  marketMakerImageUrl: z.string().nullable(),
  proposal: z.number().safe().nullable(),
  loanTokenSupplyPercentage: z.number().safe(),
  loanTokenQuantity: z.number().safe(),
  loanTokenSizeUsd: z.number().safe(),
  kpiOrderBookDominanceThreshold: z.number().safe(),
  uptimeObligationPercentage: z.number().safe().nullable(),
  kpiLoanValueDepth200BpsUsd: z.number().safe(),
  projectedLoanUtilizationPercentage: z.number().safe(),
  kpis: z.array(TierSchemaResponse),
  marketMakerComment: z.string().nullable(),
  recommendationRank: z.number().safe().nullable(),
  liquidityDistributionCex: z.number().nonnegative().max(100).default(50),
  liquidityDistributionDex: z.number().nonnegative().max(100).default(50),
  primaryCexVenue: z.string().nullable().default('binance'),
  primaryDexVenue: z.string().nullable().default('raydium'),
  aggregatedRank: z.number().nonnegative().nullable(),
  grade: z.enum(PerformanceGradeValues).nullable().default(null),
  // Matrix positioning ranks (admin-editable)
  matrixProposalQualityRank: z.number().int().default(0),
  matrixLoanSizeRank: z.number().int().default(0),
  matrixLoanPriceFeesRank: z.number().int().default(0),
  matrixKpisRank: z.number().int().default(0),
  matrixAggregatedRank: z.number().int().default(0),
  matrixDepthRank: z.number().int().default(0),
  matrixVolumeRank: z.number().int().default(0),
  matrixSpreadRank: z.number().int().default(0),
  matrixKpiRank: z.number().int().default(0),
  projectId: z.string().uuid(),
})

export const LoanCallOptionQuoteResponse = BaseQuoteSchema.extend({
  loanOptionsStyle: z.enum(OptionsStyleEnumValues),
  loanTenorMonths: z.number().safe(),
  loanInterestRatePercentage: z.number().safe().nullable(),
  kpiBidAskSpreadBps: z.number().safe(),
  tranchesCount: z.number().safe(),
  tranches: z.array(LoanCallOptionTranche),
})

export type LoanCallOptionQuote = z.infer<typeof LoanCallOptionQuoteResponse>

export const RetainerWorkingCapitalQuoteResponse = BaseQuoteSchema.extend({
  loanStablecoinSizeUsd: z.number().safe(),
  loanCustody: z.enum(MarketMakerQuoteLoanCustodyEnumValues),
  onboardingFeeUsd: z.number().safe(),
  unlimitedExchangeCoverage: z.boolean(),
  monthlyFeeUsd: z.number().safe(),
  minContractTermMonths: z.number().safe(),
  profitSharePercentage: z.number().safe(),
  unlimitedExchangeCoverageMonthlyFeeUsd: z.number().safe().nonnegative().finite(),
  singleExchangeCoverageMonthlyFeeUsd: z.number().safe().nonnegative().finite(),
})

export type RetainerWorkingCapitalQuote = z.infer<typeof RetainerWorkingCapitalQuoteResponse>

export const QuotesByEngagementTypeResponse = z.object({
  [EngagementOptionEnum.LoanCallOption]: z.array(LoanCallOptionQuoteResponse),
  [EngagementOptionEnum.RetainerWorkingCapital]: z.array(RetainerWorkingCapitalQuoteResponse),
})

export const LiquidityQuotesByEngagementTypeResponse = z.object({
  [EngagementOptionEnum.LoanCallOption]: z.array(
    LoanCallOptionQuoteResponse.omit({
      recommendationRank: true,
    }),
  ),
  [EngagementOptionEnum.RetainerWorkingCapital]: z.array(
    RetainerWorkingCapitalQuoteResponse.omit({
      recommendationRank: true,
    }),
  ),
})

export type QuotesByEngagementType = z.infer<typeof QuotesByEngagementTypeResponse>

export const OutcomeMatrixQuoteSchema = z.object({
  id: z.string().uuid(),
  engagementType: z.enum(EngagementOptionEnumValues),
  status: z.enum(MarketMakerQuoteStatusEnumValues),
  selectedForRequote: z.boolean(),
  marketMakerId: z.string().nullable(),
  // Matrix positioning ranks
  matrixProposalQualityRank: z.number().int().default(0),
  matrixLoanSizeRank: z.number().int().default(0),
  matrixLoanPriceFeesRank: z.number().int().default(0),
  matrixKpisRank: z.number().int().default(0),
  matrixAggregatedRank: z.number().int().default(0),
  matrixDepthRank: z.number().int().default(0),
  matrixVolumeRank: z.number().int().default(0),
  matrixSpreadRank: z.number().int().default(0),
  matrixKpiRank: z.number().int().default(0),
  // Filtering
  liquidityDistributionCex: z.number().nonnegative().max(100).default(50),
  liquidityDistributionDex: z.number().nonnegative().max(100).default(50),
  // Tooltip fields (populated for own quotes, null for anonymized others)
  proposal: z.number().safe().nullable(),
  marketMakerName: z.string().nullable(),
  marketMakerImageUrl: z.string().nullable(),
  loanTokenSupplyPercentage: z.number().safe().nullable(),
  loanTokenSizeUsd: z.number().safe().nullable(),
  aggregatedRank: z.number().nonnegative().nullable(),
  projectedLoanUtilizationPercentage: z.number().safe().nullable(),
  kpiLoanValueDepth200BpsUsd: z.number().safe().nullable(),
  // Engagement-specific tooltip fields (populated for own quotes)
  tranches: z
    .array(
      z.object({
        tokenSupplyPercentage: z.number().safe(),
        price: z.number().safe(),
      }),
    )
    .nullable(),
  monthlyFeeUsd: z.number().safe().nullable(),
})

export type OutcomeMatrixQuote = z.infer<typeof OutcomeMatrixQuoteSchema>

export const QuotesSelectionPreviewResponse = z.object({
  quotes: QuotesByEngagementTypeResponse,
  kpisSummary: z.array(TierSchemaResponse),
  quotesSummary: RFQQuotesSummarySchema,
  selectedQuotesSummary: RFQQuotesSummarySchema,
})

export type QuotesSelectionPreview = z.infer<typeof QuotesSelectionPreviewResponse>

export const QuoteBaseSchema = z.object({
  kpiOrderBookDominanceThreshold: z.number().safe().nonnegative().finite(),
  uptimeObligationPercentage: z.number().int().min(0).max(100).nullable(),
  marketMakerComment: z.string().nullable(),
  loanTokenSupplyPercentage: z.number().safe().positive().finite(),
  primaryCexVenue: z.string().nullable(),
  primaryDexVenue: z.string().nullable(),
  liquidityDistributionCex: z.number().nonnegative().max(100).finite(),
  liquidityDistributionDex: z.number().nonnegative().max(100).finite(),
  kpis: z
    .array(KpiResultSchema)
    .length(5)
    .refine((kpis) => new Set(kpis.map((item) => item.tier)).size === kpis.length, {
      message: 'KPI tiers must be unique',
    }),
  engagementType: z.enum(EngagementOptionEnumValues),
})

export type QuoteBaseSchemaType = z.infer<typeof QuoteBaseSchema>

export const QuoteLoanCallOptionSchemaTranche = z.object({
  trancheNumber: z.number().int().min(1).max(4),
  tokenSupplyPercentage: z.number().safe().positive().finite(),
  priceMethodology: z.enum(MarketMakerQuoteTranchePriceMethodologyEnumValues),
  priceValue: z.number().safe().nonnegative().finite(),
  twapThresholdDays: z.number().safe().nullable(),
  timePeriod: z.number().safe().int().nonnegative().finite(),
})

export const QuoteLoanCallOptionSchema = QuoteBaseSchema.extend({
  engagementType: z.literal(EngagementOptionEnum.LoanCallOption),
  loanOptionsStyle: z.enum(OptionsStyleEnumValues),
  loanTenorMonths: z.number().safe().int().nonnegative().finite(),
  loanInterestRatePercentage: z.number().safe().nonnegative().finite(),
  tranches: z
    .array(QuoteLoanCallOptionSchemaTranche)
    .min(1, 'Loan Details are required')
    .max(4)
    .refine(
      (tranches) =>
        tranches
          .toSorted((a, b) => a.trancheNumber - b.trancheNumber)
          .every((tranche, index) => tranche.trancheNumber === index + 1),
      { message: 'Tranches numbers must be sequential and start from 1' },
    ),
})

export const QuoteRetainerWorkingCapitalSchema = QuoteBaseSchema.extend({
  engagementType: z.literal(EngagementOptionEnum.RetainerWorkingCapital),
  loanStablecoinQuantity: z.number().safe().int().positive().finite(),
  dmm: QuoteDMMSchema.omit({ quoteId: true }).refine(
    (data) =>
      data.unlimitedExchangeCoverage
        ? data.unlimitedExchangeCoverageMonthlyFeeUsd > 0
        : data.singleExchangeCoverageMonthlyFeeUsd > 0,
    (data) => ({
      message: 'Number must be greater than 0',
      path: data.unlimitedExchangeCoverage
        ? ['unlimitedExchangeCoverageMonthlyFeeUsd']
        : ['singleExchangeCoverageMonthlyFeeUsd'],
    }),
  ),
})
export const QuoteCreateSchema = z
  .discriminatedUnion('engagementType', [QuoteLoanCallOptionSchema, QuoteRetainerWorkingCapitalSchema])
  .superRefine((data, ctx) => {
    if (data.engagementType === EngagementOptionEnum.LoanCallOption) {
      const sum = data.tranches.reduce((acc, tranche) => acc + tranche.tokenSupplyPercentage, 0)
      const precision = 3
      const factor = Math.pow(10, precision)
      const difference = Math.abs(Math.round((data.loanTokenSupplyPercentage - sum) * factor) / factor)

      if (isFloatLessWithPrecision(data.loanTokenSupplyPercentage, sum, precision)) {
        const msg = `The % of token supply allocated to your tranches has been exceeded by ${difference}%. The SUM of your tranches cannot exceed ${formatValue(data.loanTokenSupplyPercentage, 'percent:3')} of Token Supply.`
        ctx.addIssue({
          path: ['loanTokenSupplyPercentage'],
          message: msg,
          code: ZodIssueCode.custom,
        })

        data.tranches.forEach((_, index) => {
          ctx.addIssue({
            path: ['tranches', index, 'tokenSupplyPercentage'],
            message: msg,
            code: ZodIssueCode.custom,
          })
        })
      } else if (isFloatGreaterWithPrecision(data.loanTokenSupplyPercentage, sum, precision)) {
        const msg = `There is still ${difference}% of the total token supply % pending to be allocated on your tranches. Please adjust the values to continue.`
        ctx.addIssue({
          path: ['loanTokenSupplyPercentage'],
          message: msg,
          code: ZodIssueCode.custom,
        })

        data.tranches.forEach((_, index) => {
          ctx.addIssue({
            path: ['tranches', index, 'tokenSupplyPercentage'],
            message: msg,
            code: ZodIssueCode.custom,
          })
        })
      }
    }
  })

export const QuoteUpdateSchema = z.object({
  quoteId: z.string().uuid(),
  submit: z.boolean().default(false),
  quote: QuoteCreateSchema,
})
export type QuoteLoanCallOptionSchemaType = z.infer<typeof QuoteLoanCallOptionSchema>
export type QuoteRetainerWorkingCapitalSchemaType = z.infer<typeof QuoteRetainerWorkingCapitalSchema>
export type QuoteLoanCallOptionSchemaTrancheType = z.infer<typeof QuoteLoanCallOptionSchemaTranche>
export type QuoteCreateSchemaType = z.infer<typeof QuoteCreateSchema>
export type QuoteUpdateSchemaType = z.infer<typeof QuoteUpdateSchema>
export type QuoteDMMSchemaType = z.infer<typeof QuoteDMMSchema>

export const QuotesSummaryResponse = z.object({
  projectName: z.string(),
  tokenTicker: z.string().nullable(),
  marketMakerName: z.string(),
  loanTenorMonths: z.number().safe().nullable(),
  totalLoanQuantityTokens: z.number().safe().nullable(),
  currentTokenPrice: z.number().safe(),
  tokenPriceW2x: z.number().safe(),
  tier1Depth50Bps: z.number().safe(),
  tier1Depth100Bps: z.number().safe(),
  tier1Depth200Bps: z.number().safe(),
  tier1Spread: z.number().safe(),
  tier1Depth50BpsW2xPx: z.number().safe(),
  tier1Depth100BpsW2xPx: z.number().safe(),
  tier1Depth200BpsW2xPx: z.number().safe(),
  weightedOptionPremiumPercent: z.number().safe(),
  weightedPurchasePrice: z.number().safe(),
  weightedPurchaseAmount: z.number().safe(),
  numberOfTranches: z.number().safe(),
  tranches: z.array(
    z.object({
      percentFixedPriced: z.number().safe().nullable(),
      proceedsFixed: z.number().safe().nullable(),
      percentDynamicPrice: z.number().safe().nullable(),
      twapThreshold: z.number().safe().nullable(),
      timePeriod: z.number().safe().nullable(),
      priceW2x: z.number().safe().nullable(),
      proceedsDynamic: z.number().safe().nullable(),
      quantity: z.number().safe().nullable(),
    }),
  ),
  totalProceeds: z.number().safe(),
  minimumDepth50Bps: z.number().safe(),
  minimumDepth100Bps: z.number().safe(),
  minimumDepth200Bps: z.number().safe(),
  minimumSpread: z.number().safe(),
  maximumDepth50Bps: z.number().safe(),
  maximumDepth100Bps: z.number().safe(),
  maximumDepth200Bps: z.number().safe(),
  maximumSpread: z.number().safe(),
  orderBookDominance: z.number().safe(),
  totalLoanQuantityStablecoins: z.number().safe().nullable(),
  minimumContractTermMonths: z.number().safe().nullable(),
  onboardingFee: z.number().safe().nullable(),
  monthlyFeePerExchange: z.number().safe().nullable(),
  monthlyFeeUnlimitedExchanges: z.number().safe().nullable(),
  profitSharePercent: z.number().safe().nullable(),
})

export type QuotesSummaryType = z.infer<typeof QuotesSummaryResponse>

export const QuoteDetailedSummary = QuotesSummaryResponse.extend({
  dateOfQuoteSelection: z.string().nullable(),
  engagementType: z.enum(EngagementOptionEnumValues),
  loanOptionsStyle: z.enum(OptionsStyleEnumValues).nullable(),
  unlimitedExchangeCoverage: z.boolean(),
  kpiOrderBookDominanceThreshold: z.number().safe().nullable(),
  loanInterestRatePercentage: z.number().safe().nullable(),
  loanSizeUsd: z.number().safe().nullable(),
  loanStablecoinSizeUsd: z.number().safe().nullable(),
  loanTokenQuantity: z.number().safe().nullable(),
  loanTokenSupplyPercentage: z.number().safe().nullable(),
  loanDetails: z
    .array(
      z.object({
        trancheNumber: z.number().safe().int().min(0).finite(),
        tokenSupplyPercentage: z.number().safe(),
        priceMethodology: z.enum(MarketMakerQuoteTranchePriceMethodologyEnumValues),
        price: z.number().safe(),
        priceFlag: z.enum(MMRFQTranchePriceFlagEnumValues),
        twapThresholdDays: z.number().safe().nullable(),
        timePeriod: z.number().safe(),
      }),
    )
    .optional(),
  comments: z.string().nullable(),
  kpis: z.array(TierSchemaResponse),
  proposal: z.number().safe(),
  loanCustody: z.enum(MarketMakerQuoteLoanCustodyEnumValues).nullable(),
})

export type QuoteDetailedSummaryType = z.infer<typeof QuoteDetailedSummary>

export const RFQSurveySchema = z.object({
  leaderboardRankInfluence: likertScaleTypeSchema,
  loanSizeImportance: likertScaleTypeSchema,
  liquidityKpiInfluence: likertScaleTypeSchema,
  loanPricingSensitivity: likertScaleTypeSchema,
  feesInfluence: likertScaleTypeSchema,
  profitShareInfluence: likertScaleTypeSchema,
  improvedLiquidityKpisAcceptance: likertScaleTypeSchema.optional().nullable().default(null),
  lessFavorablePricingAcceptance: likertScaleTypeSchema.optional().nullable().default(null),
})

// TODO: make survey required when it will be done.
export const RFQAcceptSchema = z.object({
  round: z.union([z.literal(1), z.literal(2)]),
  quoteIds: z.array(z.string().uuid()).min(1, 'Provide at least one quote to accept.'),
  survey: RFQSurveySchema.optional(),
})

export const RFQCancelSchema = z.object({
  reason: z.string().optional(),
})

export const RFQRequoteSchema = z.object({
  quoteIds: z.array(z.string().uuid()).min(1, 'Provide at least one quote to requote.'),
  survey: RFQSurveySchema.optional(),
})

export type RFQAcceptSchemaType = z.infer<typeof RFQAcceptSchema>
export type RFQCancelSchemaType = z.infer<typeof RFQCancelSchema>
export type RFQRequoteSchemaType = z.infer<typeof RFQRequoteSchema>
export type RFQSurveySchemaType = z.infer<typeof RFQSurveySchema>

export const ChartQuoteType = z.enum(['your-quotes', 'selected-by-project', 'your-quotes-selected', 'all-others'])
export type ChartQuoteTypeValue = z.infer<typeof ChartQuoteType>

const TokenLoanSizeCell = z.object({
  type: ChartQuoteType,
  submittedAt: customDateSchema,
  loanSize: z.number().safe(),
})
const TokenLoanSizeBucket = z.object({
  start: z.number().safe(),
  end: z.number().safe(),
  cells: z.array(TokenLoanSizeCell),
})

const AvgWeightedOptionPremiumCell = z.object({
  submittedAt: customDateSchema,
  optionPremium: z.number().safe(),
  type: ChartQuoteType,
})
const AvgWeightedOptionPremiumBucket = z.object({
  start: z.number().safe(),
  end: z.number().safe(),
  cells: z.array(AvgWeightedOptionPremiumCell),
})

const SpreadCell = z.object({
  type: ChartQuoteType,
  submittedAt: customDateSchema,
  spread: z.number().safe(),
})
const SpreadBucket = z.object({
  start: z.number().safe(),
  end: z.number().safe(),
  cells: z.array(SpreadCell),
})

const DepthCell = z.object({
  submittedAt: customDateSchema,
  depthUsd: z.number().safe(),
  depthPercentOfLoan: z.number().safe(),
  type: ChartQuoteType,
})
const DepthBucket = z.object({
  startUsd: z.number().safe(),
  endUsd: z.number().safe(),
  startPercent: z.number().safe(),
  endPercent: z.number().safe(),
  cells: z.array(DepthCell),
})

const OrderBookDominanceCell = z.object({
  submittedAt: customDateSchema,
  orderBookDominance: z.number().safe(),
  type: ChartQuoteType,
})
const OrderBookDominanceBucket = z.object({
  start: z.number().safe(),
  end: z.number().safe(),
  cells: z.array(OrderBookDominanceCell),
})

const NumberOfQuotesPerTrancheCell = z.object({
  submittedAt: customDateSchema,
  numberOfTranches: z.number().safe(),
  type: ChartQuoteType,
})
const NumberOfQuotesPerTrancheBin = z.object({
  tranche: z.number().safe(),
  cells: z.array(NumberOfQuotesPerTrancheCell),
})

const OptionPricingTranche = z.object({
  tranche: z.number().safe(),
  min: z.number().safe(),
  q1: z.number().safe(),
  median: z.number().safe(),
  q3: z.number().safe(),
  max: z.number().safe(),
})

const TranchePricingMethodologyData = z.object({
  tranche: z.number().safe(),
  fixedPercentage: z.number().safe(),
  fixedCount: z.number().int(),
  totalCount: z.number().int(),
})

const DepthByViewMode = z.object({
  byUsd: z.array(DepthBucket),
  byPercent: z.array(DepthBucket),
})

const DepthByBps = z.object({
  '50bps': DepthByViewMode,
  '100bps': DepthByViewMode,
  '200bps': DepthByViewMode,
})

const StablecoinLoanSizeCell = z.object({
  type: ChartQuoteType,
  submittedAt: customDateSchema,
  stablecoinLoanSizeUsd: z.number().safe(),
})
const StablecoinLoanSizeBucket = z.object({
  start: z.number().safe(),
  end: z.number().safe(),
  cells: z.array(StablecoinLoanSizeCell),
})

const ExchangeCoverageBreakdown = z.object({
  perExchangeCount: z.number().int(),
  unlimitedCoverageCount: z.number().int(),
  perExchangePercentage: z.number().safe(),
  unlimitedCoveragePercentage: z.number().safe(),
  perExchangeSelectedCount: z.number().int().optional(),
  unlimitedCoverageSelectedCount: z.number().int().optional(),
})

const PerExchangeCoverageFeeCell = z.object({
  type: ChartQuoteType,
  submittedAt: customDateSchema,
  monthlyFeeUsd: z.number().safe(),
})
const PerExchangeCoverageFeeBucket = z.object({
  start: z.number().safe(),
  end: z.number().safe(),
  cells: z.array(PerExchangeCoverageFeeCell),
})

const UnlimitedCoverageFeeCell = z.object({
  type: ChartQuoteType,
  submittedAt: customDateSchema,
  monthlyFeeUsd: z.number().safe(),
})
const UnlimitedCoverageFeeBucket = z.object({
  start: z.number().safe(),
  end: z.number().safe(),
  cells: z.array(UnlimitedCoverageFeeCell),
})

const OnboardingFeeCell = z.object({
  type: ChartQuoteType,
  submittedAt: customDateSchema,
  onboardingFeeUsd: z.number().safe(),
})
const OnboardingFeeBucket = z.object({
  start: z.number().safe(),
  end: z.number().safe(),
  cells: z.array(OnboardingFeeCell),
})

const ProfitShareCell = z.object({
  type: ChartQuoteType,
  submittedAt: customDateSchema,
  profitSharePercentage: z.number().safe(),
})
const ProfitShareBucket = z.object({
  start: z.number().safe(),
  end: z.number().safe(),
  cells: z.array(ProfitShareCell),
})

export const RFQProposalBenchmarkingResponse = z.object({
  totalQuotesReceived: z.number().int(),
  loanCallOptionQuotes: z.number().int(),
  retainerWorkingCapitalQuotes: z.number().int(),
  tokenLoanSize: z.array(TokenLoanSizeBucket),
  avgWeightedOptionPremium: z.array(AvgWeightedOptionPremiumBucket),
  spread: z.record(z.enum(KpiTierEnumValues), z.array(SpreadBucket)),
  depth: z.record(z.enum(KpiTierEnumValues), DepthByBps),
  orderBookDominance: z.array(OrderBookDominanceBucket),
  numberOfQuotesPerTranche: z.array(NumberOfQuotesPerTrancheBin),
  optionPricingBreakdown: z.array(OptionPricingTranche),
  tranchePricingMethodology: z.array(TranchePricingMethodologyData),
  stablecoinLoanSize: z.array(StablecoinLoanSizeBucket),
  exchangeCoverageBreakdown: ExchangeCoverageBreakdown,
  perExchangeCoverageFee: z.array(PerExchangeCoverageFeeBucket),
  unlimitedCoverageFee: z.array(UnlimitedCoverageFeeBucket),
  onboardingFees: z.array(OnboardingFeeBucket),
  profitShare: z.array(ProfitShareBucket),
})

/**
 * RFQ demo snapshot served by the public demo endpoint. Each section mirrors the
 * body of the corresponding project read endpoint (details, round, quotes
 * breakdown) so the demo reuses the real Round-1 rendering path.
 */
export const RfqDemoSnapshotDataSchema = z.object({
  details: RFQDetailsResult,
  round: RFQRoundDetailsResponse,
  quotes: z.object({ quotes: QuotesByEngagementTypeResponse }),
  /**
   * Pre-computed "Explain this quote" template vars (keyed by quote id), captured
   * so the drawer works without the authenticated quote-summary endpoint.
   * Optional for backwards compatibility with older snapshots.
   */
  quoteSummaries: z.record(z.string().uuid(), QuotesSummaryResponse).optional(),
  /**
   * Pre-computed, anonymized "Market Maker Rank & Profile" content keyed by demo
   * MM alias id, so the demo renders entirely from this document — nothing
   * resolves against live market makers. Sections are null for MMs without
   * leaderboard presence, mirroring the live page.
   */
  marketMakerProfiles: z
    .record(
      z.string(),
      z.object({
        details: MarketMakerDetailsSchema.nullable(),
        performance: PerformanceMetricsForMmResponseSchema.nullable(),
        breakdown: LeaderboardBreakdownResponseSchema.nullable(),
      }),
    )
    .optional(),
})

export type RfqDemoSnapshotData = z.infer<typeof RfqDemoSnapshotDataSchema>

export const RfqDemoSnapshotMetaResult = z.object({
  id: z.string().uuid(),
  status: z.enum(['draft', 'published']),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type RfqDemoSnapshotMeta = z.infer<typeof RfqDemoSnapshotMetaResult>
